import {
  AddSlotDto,
  CalendarDto,
  MoveSlotDto,
  PieceSummaryDto,
  PlanIdeaDto,
  SlotDto,
  TodayDto,
  WeekPlanDto,
} from "@growth/dto";
import { ContentFormat } from "@growth/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

/** Anything being drafted or planned is polled until it settles. */
const working = (calendar: CalendarDto | undefined) =>
  Boolean(
    calendar?.plans.some((plan) => plan.status === "planning") ||
    calendar?.slots.some((slot) => slot.piece?.status === "drafting"),
  );

export const useCalendar = (from: string, to: string) =>
  useQuery({
    queryKey: ["calendar", from, to],
    queryFn: async () => (await api.get<CalendarDto>("/calendar", { params: { from, to } })).data,
    refetchInterval: (query) => (working(query.state.data) ? 2500 : 30_000),
  });

const useRefreshCalendar = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["calendar"] });
    void queryClient.invalidateQueries({ queryKey: ["today"] });
    void queryClient.invalidateQueries({ queryKey: ["pieces"], exact: true });
  };
};

export const useRequestPlan = () => {
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async (input: { weekOf: string; note?: string }) =>
      (await api.post<WeekPlanDto>("/calendar/plans", input)).data,
    onSuccess: refresh,
  });
};

export const useApprovePlan = () => {
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<CalendarDto>(`/calendar/plans/${id}/approve`)).data,
    onSuccess: refresh,
  });
};

export const useAddSlot = () => {
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async (input: AddSlotDto) =>
      (await api.post<SlotDto>("/calendar/slots", input)).data,
    onSuccess: refresh,
  });
};

export const useMoveSlot = () => {
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async ({ id, ...input }: MoveSlotDto & { id: string }) =>
      (await api.patch<SlotDto>(`/calendar/slots/${id}`, input)).data,
    onSuccess: refresh,
  });
};

export const useCancelSlot = () => {
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.post<SlotDto>(`/calendar/slots/${id}/cancel`)).data,
    onSuccess: refresh,
  });
};

export const usePlanIdea = () => {
  const queryClient = useQueryClient();
  const refresh = useRefreshCalendar();
  return useMutation({
    mutationFn: async ({ id, ...input }: PlanIdeaDto & { id: string }) =>
      (await api.post<SlotDto[]>(`/ideas/${id}/plan`, input)).data,
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: ["ideas"] });
      void queryClient.invalidateQueries({ queryKey: ["briefs"] });
    },
  });
};

export const useToday = () =>
  useQuery({
    queryKey: ["today"],
    queryFn: async () => (await api.get<TodayDto>("/today")).data,
    refetchInterval: 10_000,
  });

export const useAddVersion = (pieceId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (format: ContentFormat) =>
      (await api.post<PieceSummaryDto>(`/pieces/${pieceId}/versions`, { format })).data,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pieces"] });
    },
  });
};
